import { useState } from 'react';

import { ImageLoaderProps } from 'next/image';
import { twMerge } from 'tailwind-merge';

import { useRutilusQuery } from '@graphql/client/useRutilusQuery';
import { useAnalytics } from '@domain/analytics/AnalyticsContext';
import { trackEvent } from '@domain/analytics/trackEvent';
import { useIsMobileScreenWidthOrThinner } from '@hooks/useScreenWidth';
import { getImgProxyLoader } from '@lib/images/imgProxyLoader';
import { getSpeciesUrl } from '@domain/routes/urls';
import { EventMykissCardClicked } from '@generated/events';
import { IBoundingBox } from '@app-types/geo';
import { getWatersBySpecies } from '@graphql/queries/rutilus/Species';
import { AppDownloadModal } from '../../AppDownloadModal/AppDownloadModal';
import { Button } from '../../Clickables/Buttons/Button';
import { InternalLink } from '../../Clickables/InternalLink/InternalLink';
import { ArrowRightIcon } from '../../Icons/ArrowRightIcon';
import { FishCatchesIcon } from '../../Icons/FishCatchesIcon';
import { NextImageWrapper } from '../../NextImage/NextImageWrapper';
import appdownloadPhone from './assets/AppdownloadPhone.png';
import appdownloadPhoneMobile from './assets/AppdownloadPhoneMobile.png';
import { skipToken } from '@apollo/client/react';

interface IProps {
  boundingBox?: IBoundingBox;
  name: string;
  nrOfFishingSpots?: number;
  speciesId: string;
  testId?: string;
  /* An identifier for where this carousel is being used, e.g. species-top-waters */
  clickEventSource: EventMykissCardClicked['properties']['source'];
  /* An ID of the resource type where this carousel is being used, e.g. a species ID if it's on a species page */
  clickEventSourceId?: EventMykissCardClicked['properties']['source_id'];
  /* The position of the card in a list (if applicable) */
  clickEventPosition?: EventMykissCardClicked['properties']['position'];
  className?: string;
  imageClassName?: string;
  imageUrl?: string;
  imageWidth?: number;
  imageHeight?: number;
  useCustomLoader?: boolean;
}

export const TopSpeciesCard = ({
  boundingBox,
  speciesId,
  testId,
  name,
  clickEventSource,
  nrOfFishingSpots,
  clickEventPosition,
  clickEventSourceId,
  className = '',
  imageClassName = '',
  imageUrl,
  imageWidth,
  imageHeight,
  useCustomLoader = true,
}: IProps) => {
  const [isAppDownloadModalOpen, setIsAppDownloadModalOpen] = useState(false);
  const isMobileScreenWidthOrThinner = useIsMobileScreenWidthOrThinner();
  const { currentPage } = useAnalytics();
  const imgProxyLoader = ({ src, width, quality }: ImageLoaderProps): string =>
    getImgProxyLoader(src, width, imageWidth ?? 0, imageHeight, { quality });

  const handleSpeciesClick = () => {
    trackEvent({
      name: 'mykiss_card_clicked',
      properties: {
        source: clickEventSource,
        target: 'species',
        target_id: speciesId,
        position: clickEventPosition,
        source_id: clickEventSourceId,
        page: currentPage,
      },
    });
  };

  const handleTopWatersClick = () => {
    trackEvent({
      name: 'mykiss_cta_button_clicked',
      properties: {
        source: clickEventSource,
        source_id: speciesId,
        position: clickEventPosition,
        page: currentPage,
        type: 'downloadModal',
      },
    });
    setIsAppDownloadModalOpen(true);
  };

  const shouldSkip = nrOfFishingSpots !== undefined || boundingBox === undefined;

  const { loading, data } = useRutilusQuery(
    getWatersBySpecies,
    shouldSkip
      ? skipToken
      : {
          variables: {
            boundingBox,
            speciesIDs: [speciesId],
          },
        },
  );

  const hasFishingSpots: boolean =
    (nrOfFishingSpots !== undefined && nrOfFishingSpots > 0) ||
    (data?.entry.totalCount !== undefined && data.entry.totalCount > 0);

  return (
    <div
      className={twMerge(
        'border-b-twilight relative w-full border-b py-3 pr-2 md:py-2.5',
        className,
      )}
      data-testid={testId}
    >
      <div className="relative flex items-center gap-4">
        {imageUrl && imageWidth && imageHeight ? (
          <NextImageWrapper
            wrapperClassName="w-32 h-12 md:h-11 md:w-28"
            imageClassName={twMerge('size-full object-contain', imageClassName)}
            customPlaceholderIcon={
              <FishCatchesIcon className="size-full object-contain" size={93} />
            }
            customPlaceholderClassName="text-twilight p-0 bg-sea-foam sm:bg-lake-fog sm:text-twilight"
            alt={name}
            src={imageUrl}
            loader={useCustomLoader ? imgProxyLoader : undefined}
            fill
            sizes="(min-width: 990px) 128vw, 112px"
          />
        ) : (
          <div className="bg-twilight text-lake-fog mb-3 flex h-11 w-28 items-center justify-center rounded-md md:rounded-lg">
            <FishCatchesIcon size={93} />
          </div>
        )}
        <div className="flex flex-col gap-3 md:gap-2">
          <InternalLink
            to={getSpeciesUrl(speciesId, name)}
            variant="textNone"
            onClick={handleSpeciesClick}
            className="body-text-sm m-0 flex items-center gap-1 font-semibold"
            size="xs"
          >
            {name}
            <ArrowRightIcon width={16} height={16} />
          </InternalLink>
          {loading && <div className="bg-twilight h-4 w-32 animate-pulse rounded" />}
          {hasFishingSpots && (
            <Button
              onClick={handleTopWatersClick}
              variant="buttonSecondary"
              className="w-32 px-2 text-xs"
              size="xs"
            >
              {nrOfFishingSpots ?? data?.entry.totalCount} fishing spots
            </Button>
          )}
        </div>
      </div>
      <AppDownloadModal
        appDownloadSource="TopSpeciesCard"
        appDownloadSourceId={speciesId}
        closeButtonVariant={isMobileScreenWidthOrThinner ? 'textNone' : 'controlWhiteBg'}
        modalHeader={'Know where to fish – find spots by species'}
        modalHeaderClassName="sm:mr-16 md:mr-16"
        modalHeaderVariant="sm"
        modalText=""
        isModalOpen={isAppDownloadModalOpen}
        image={appdownloadPhone}
        imageMobile={appdownloadPhoneMobile}
        imageWrapperClassName="bg-tuna-tint px-8 sm:p-6 md:mr-4"
        onCloseModal={() => {
          setIsAppDownloadModalOpen(false);
        }}
        textWrapperClassName="mt-6 sm:pb-0 md:pr-4 md:pb-4"
        showQRCodeHint
        width="700px"
      />
    </div>
  );
};
